import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth-context'
import { Bell } from 'lucide-react'

/**
 * NotificationBadge — a floating bell icon pinned below the cherry
 * balance chip in the top-right corner. Shows a red badge with unread
 * notification count. Subscribes to Supabase Realtime for live updates.
 */
export default function NotificationBadge() {
  const { user } = useAuth()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [unreadCount, setUnreadCount] = useState(0)

  useEffect(() => {
    if (!user) return

    // 1. Initial fetch of unread count
    async function fetchUnreadCount() {
      // Dual-schema: unread = legacy read is not true AND new read_at is null
      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .or('read.is.null,read.eq.false')
        .is('read_at', null)

      if (!error && count !== null) {
        setUnreadCount(count)
      }
    }
    fetchUnreadCount()

    // 2. Realtime subscription for live updates
    const channel = supabase
      .channel('notification-badge-' + user.id)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Re-fetch count on any change
          fetchUnreadCount()
        }
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user])

  if (!user) return null

  return (
    <button
      onClick={() => navigate('/notifications')}
      className="fixed right-3 top-16 z-40 flex h-10 w-10 items-center justify-center rounded-full border border-silver bg-white/90 shadow-sm backdrop-blur-sm transition-all hover:scale-105 active:scale-95"
      aria-label={t('notifications.badge_aria', { count: unreadCount })}
    >
      <Bell className="h-4 w-4 text-ink" />
      {unreadCount > 0 && (
        <span className="absolute -right-0.5 -top-0.5 flex h-4.5 w-4.5 items-center justify-center rounded-full bg-terra text-[9px] font-bold text-white shadow-sm">
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      )}
    </button>
  )
}
